import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { Activity, TrendingUp, Shield, AlertOctagon, Users2, Mic } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { Pill } from "../ui-prim";
import { AIPanel } from "../AIPanel";
import { prompts } from "../../lib/ai";
import { S4_DEFAULTS, STATUS_TO_TONE, nextStatus } from "../../lib/s4hanaSeed";

// ─── helpers ─────────────────────────────────────────────────────────────
const TONE_HEX = {
  green: "#16A34A", amber: "#D97706", red: "#DC2626",
  blue: "#0EA5E9",  purple: "#8B5CF6", neutral: "#71717A",
};
const barColorFor = (v) => v >= 70 ? "#0369A1" : v >= 50 ? "#D97706" : "#DC2626";

// Deep-merge s4hana doc with defaults — preserves the user's edits but adds new keys.
function mergeS4(saved) {
  return { ...S4_DEFAULTS, ...(saved || {}) };
}

const SUBTABS = [
  { id: "ph",  label: "Program Health",     icon: Activity      },
  { id: "vr",  label: "Value Realization",  icon: TrendingUp    },
  { id: "gov", label: "Governance",         icon: Shield        },
  { id: "ri",  label: "Risk & Issues",      icon: AlertOctagon  },
  { id: "ca",  label: "Change & Adoption",  icon: Users2        },
  { id: "ws",  label: "Workshop Leadership",icon: Mic           },
];

// ─── tiny editable primitives ─────────────────────────────────────────────
function Editable({ value, onChange, className = "", disabled, multiline = false }) {
  return (
    <span
      contentEditable={!disabled}
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={(e) => {
        const v = multiline ? e.currentTarget.innerText : e.currentTarget.textContent;
        if (v !== value) onChange(v);
      }}
      className={`outline-none focus:bg-[var(--surface)] focus:ring-1 focus:ring-[var(--navy)] rounded-sm px-1 -mx-1 ${className}`}
    >
      {value}
    </span>
  );
}

function KCard({ label, value, sub, tone = "neutral", onChange, onChangeSub, readOnly }) {
  return (
    <div className="card p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.1em] text-zinc-500">{label}</div>
      <div className="font-display text-xl md:text-2xl font-extrabold tracking-tight text-zinc-900 mt-0.5">
        <Editable value={value} onChange={onChange} disabled={readOnly}/>
      </div>
      {sub !== undefined && (
        <div className="font-mono text-[10px] mt-0.5" style={{ color: TONE_HEX[tone] || "#71717A" }}>
          <Editable value={sub} onChange={onChangeSub} disabled={readOnly}/>
        </div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-3">
      {children}
    </div>
  );
}

// ─── ROOT ────────────────────────────────────────────────────────────────
export default function S4Hana() {
  const { doc, mutate, readOnly } = useProject();
  const data = useMemo(() => mergeS4(doc.s4hana), [doc.s4hana]);
  const [sub, setSub] = useState("ph");

  const patch = (updater) => {
    if (readOnly) return;
    mutate(d => ({ ...d, s4hana: { ...mergeS4(d.s4hana), ...(typeof updater === "function" ? updater(mergeS4(d.s4hana)) : updater) } }));
  };

  const setField = (key, value) => patch({ [key]: value });
  const setArrayItem = (key, idx, value) =>
    patch(prev => ({ [key]: prev[key].map((it, i) => i === idx ? { ...it, ...value } : it) }));

  const ActiveSub = ({ id, children }) => sub === id ? children : null;

  return (
    <>
      {/* HERO */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">
            Executive View
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">
            <Editable value={data.programName} onChange={(v) => setField("programName", v)} disabled={readOnly}/>
          </h2>
          <div className="font-mono text-[11px] text-zinc-500 mt-1">
            <Editable value={data.programTagline} onChange={(v) => setField("programTagline", v)} disabled={readOnly}/>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[11px] text-zinc-500">
            <Editable value={data.period} onChange={(v) => setField("period", v)} disabled={readOnly}/>
          </div>
          <div className="font-mono text-[11px] text-zinc-500">
            <Editable value={data.scope} onChange={(v) => setField("scope", v)} disabled={readOnly}/>
          </div>
          {!readOnly && (
            <div className="font-mono text-[10px] text-zinc-400 mt-1">Click any value to edit · auto-saves</div>
          )}
        </div>
      </div>

      {/* SUMMARY STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-5" data-testid="s4-summary">
        {data.summary.map((s, i) => (
          <KCard key={i}
            label={s.label} value={s.value} sub={s.trend} tone={s.tone}
            onChange={(v) => setArrayItem("summary", i, { value: v })}
            onChangeSub={(v) => setArrayItem("summary", i, { trend: v })}
            readOnly={readOnly}/>
        ))}
      </div>

      {/* AI EXECUTIVE BRIEF */}
      <AIPanel
        title="Executive Brief"
        section="s4hana"
        buildPrompt={() => prompts.s4hanaBrief(doc, data)}
        buttonLabel="Generate Brief"/>

      {/* SUBTABS */}
      <div className="border-b border-[var(--border)] mb-5">
        <div className="flex gap-1 overflow-x-auto">
          {SUBTABS.map(t => {
            const Icon = t.icon;
            const on = sub === t.id;
            return (
              <button key={t.id}
                onClick={() => setSub(t.id)}
                data-testid={`s4-subtab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-[11px] font-mono uppercase tracking-[0.1em] whitespace-nowrap transition-colors border-b-2
                  ${on ? "border-[var(--navy)] text-[var(--navy)]" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
                <Icon size={13} strokeWidth={1.8}/>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── PROGRAM HEALTH ─── */}
      <ActiveSub id="ph">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-4">
            <SectionTitle>Workstream completion</SectionTitle>
            <div className="space-y-2">
              {data.workstreams.map((w, i) => (
                <div key={i} className="grid grid-cols-[150px_1fr_44px] items-center gap-2.5">
                  <div className="text-[11.5px] text-zinc-700 truncate">
                    <Editable value={w.name} onChange={(v) => setArrayItem("workstreams", i, { name: v })} disabled={readOnly}/>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full bar-fill" style={{ width: `${w.pct}%`, background: barColorFor(w.pct) }}/>
                  </div>
                  <div className="text-[11.5px] font-mono font-semibold text-right">
                    <Editable value={`${w.pct}%`} onChange={(v) => {
                      const n = parseInt(v); if (!isNaN(n)) setArrayItem("workstreams", i, { pct: Math.max(0, Math.min(100, n)) });
                    }} disabled={readOnly}/>
                  </div>
                </div>
              ))}
            </div>
            <hr className="border-[var(--border)] my-4"/>
            <div className="grid grid-cols-3 gap-2">
              {data.designMetrics.map((m, i) => (
                <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
                  onChange={(v) => setArrayItem("designMetrics", i, { value: v })}
                  onChangeSub={(v) => setArrayItem("designMetrics", i, { sub: v })}
                  readOnly={readOnly}/>
              ))}
            </div>
          </div>

          <div className="card p-4">
            <SectionTitle>Phase milestone completion</SectionTitle>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart data={data.phaseData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                  <XAxis dataKey="phase" stroke="var(--tx3)" fontSize={11}/>
                  <YAxis stroke="var(--tx3)" fontSize={11} tickFormatter={v => `${v}%`} domain={[0, 100]}/>
                  <Tooltip
                    contentStyle={{ background: "var(--paper)", border: "1px solid var(--border)", fontSize: 12 }}
                    formatter={(v) => `${v}%`}/>
                  <Bar dataKey="done" fill="#0369A1" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <hr className="border-[var(--border)] my-4"/>
            <SectionTitle>Clean core & technical governance</SectionTitle>
            <div className="grid grid-cols-2 gap-2">
              {data.techMetrics.map((m, i) => (
                <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
                  onChange={(v) => setArrayItem("techMetrics", i, { value: v })}
                  onChangeSub={(v) => setArrayItem("techMetrics", i, { sub: v })}
                  readOnly={readOnly}/>
              ))}
            </div>
          </div>
        </div>
      </ActiveSub>

      {/* ─── VALUE REALIZATION ─── */}
      <ActiveSub id="vr">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          {data.valueKpis.map((m, i) => (
            <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
              onChange={(v) => setArrayItem("valueKpis", i, { value: v })}
              onChangeSub={(v) => setArrayItem("valueKpis", i, { sub: v })}
              readOnly={readOnly}/>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-4">
            <SectionTitle>Benefits realization — target vs actual ($M)</SectionTitle>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart data={data.benefits} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                  <XAxis dataKey="area" stroke="var(--tx3)" fontSize={10}/>
                  <YAxis stroke="var(--tx3)" fontSize={11} tickFormatter={v => `$${v}M`}/>
                  <Tooltip
                    contentStyle={{ background: "var(--paper)", border: "1px solid var(--border)", fontSize: 12 }}
                    formatter={(v) => `$${v}M`}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                  <Bar dataKey="target"   name="Target $M"   fill="#BAE6FD" radius={[4,4,0,0]}/>
                  <Bar dataKey="realized" name="Realized $M" fill="#0369A1" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card p-4">
            <SectionTitle>Value KPI tracker — click status to cycle</SectionTitle>
            <table className="tbl">
              <thead>
                <tr><th>Process Area</th><th>Target</th><th>Actual</th><th>Status</th></tr>
              </thead>
              <tbody>
                {data.valueTracker.map((r, i) => (
                  <tr key={i}>
                    <td><Editable value={r.area} onChange={(v) => setArrayItem("valueTracker", i, { area: v })} disabled={readOnly}/></td>
                    <td className="font-mono text-[11px]"><Editable value={r.target} onChange={(v) => setArrayItem("valueTracker", i, { target: v })} disabled={readOnly}/></td>
                    <td className="font-mono text-[11px]"><Editable value={r.actual} onChange={(v) => setArrayItem("valueTracker", i, { actual: v })} disabled={readOnly}/></td>
                    <td>
                      <button type="button" disabled={readOnly} onClick={() => setArrayItem("valueTracker", i, { status: nextStatus(r.status) })}>
                        <Pill tone={STATUS_TO_TONE[r.status] || "neutral"}>{r.status}</Pill>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ActiveSub>

      {/* ─── GOVERNANCE ─── */}
      <ActiveSub id="gov">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          {data.govKpis.map((m, i) => (
            <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
              onChange={(v) => setArrayItem("govKpis", i, { value: v })}
              onChangeSub={(v) => setArrayItem("govKpis", i, { sub: v })}
              readOnly={readOnly}/>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-4">
            <SectionTitle>Governance health indicators — click to cycle</SectionTitle>
            <table className="tbl">
              <thead><tr><th>Indicator</th><th>Score</th><th>Status</th></tr></thead>
              <tbody>
                {data.governance.map((g, i) => (
                  <tr key={i}>
                    <td><Editable value={g.ind}   onChange={(v) => setArrayItem("governance", i, { ind: v })} disabled={readOnly}/></td>
                    <td className="font-mono text-[11px]"><Editable value={g.score} onChange={(v) => setArrayItem("governance", i, { score: v })} disabled={readOnly}/></td>
                    <td>
                      <button type="button" disabled={readOnly} onClick={() => setArrayItem("governance", i, { status: nextStatus(g.status, "governance") })}>
                        <Pill tone={STATUS_TO_TONE[g.status] || "neutral"}>{g.status}</Pill>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <hr className="border-[var(--border)] my-4"/>
            <div className="grid grid-cols-3 gap-2">
              {data.templateMetrics.map((m, i) => (
                <KCard key={i} label={m.label} value={m.value}
                  onChange={(v) => setArrayItem("templateMetrics", i, { value: v })}
                  readOnly={readOnly}/>
              ))}
            </div>
          </div>
          <div className="card p-4">
            <SectionTitle>Decision velocity trend (avg days)</SectionTitle>
            <div style={{ width: "100%", height: 180 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <LineChart data={data.decisionTrend} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                  <XAxis dataKey="m" stroke="var(--tx3)" fontSize={11}/>
                  <YAxis stroke="var(--tx3)" fontSize={11} tickFormatter={v => `${v}d`} domain={[4, 11]}/>
                  <Tooltip
                    contentStyle={{ background: "var(--paper)", border: "1px solid var(--border)", fontSize: 12 }}
                    formatter={(v) => `${v} days`}/>
                  <Line type="monotone" dataKey="d" stroke="#0369A1" strokeWidth={2.4} dot={{ fill: "#0369A1", r: 4 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
            <hr className="border-[var(--border)] my-4"/>
            <SectionTitle>Pending decisions by workstream</SectionTitle>
            <div className="space-y-2">
              {data.pendingDecisions.map((p, i) => (
                <div key={i} className="grid grid-cols-[130px_1fr_70px] items-center gap-2.5">
                  <div className="text-[11.5px] text-zinc-700">
                    <Editable value={p.ws} onChange={(v) => setArrayItem("pendingDecisions", i, { ws: v })} disabled={readOnly}/>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full bar-fill" style={{ width: `${p.pct}%`, background: TONE_HEX[p.tone] || "#0369A1" }}/>
                  </div>
                  <div className="text-[11px] font-mono text-right">
                    <Editable value={p.count} onChange={(v) => setArrayItem("pendingDecisions", i, { count: v })} disabled={readOnly}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ActiveSub>

      {/* ─── RISK & ISSUES ─── */}
      <ActiveSub id="ri">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          {data.riskKpis.map((m, i) => (
            <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
              onChange={(v) => setArrayItem("riskKpis", i, { value: v })}
              onChangeSub={(v) => setArrayItem("riskKpis", i, { sub: v })}
              readOnly={readOnly}/>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-4">
            <SectionTitle>Risks by category</SectionTitle>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart layout="vertical" data={data.riskBuckets} margin={{ top: 8, right: 10, left: 30, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                  <XAxis type="number" stroke="var(--tx3)" fontSize={11}/>
                  <YAxis type="category" dataKey="cat" stroke="var(--tx3)" fontSize={10.5} width={140}/>
                  <Tooltip contentStyle={{ background: "var(--paper)", border: "1px solid var(--border)", fontSize: 12 }}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                  <Bar dataKey="critical" name="Critical" stackId="s" fill="#DC2626"/>
                  <Bar dataKey="high"     name="High"     stackId="s" fill="#D97706"/>
                  <Bar dataKey="med"      name="Medium"   stackId="s" fill="#BAE6FD"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card p-4">
            <SectionTitle>Top risks — click severity to cycle</SectionTitle>
            <table className="tbl">
              <thead><tr><th>Risk Description</th><th>Severity</th></tr></thead>
              <tbody>
                {data.topRisks.map((r, i) => (
                  <tr key={i}>
                    <td><Editable value={r.desc} onChange={(v) => setArrayItem("topRisks", i, { desc: v })} disabled={readOnly}/></td>
                    <td>
                      <button type="button" disabled={readOnly} onClick={() => setArrayItem("topRisks", i, { severity: nextStatus(r.severity, "severity") })}>
                        <Pill tone={STATUS_TO_TONE[r.severity] || "neutral"}>{r.severity}</Pill>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <hr className="border-[var(--border)] my-4"/>
            <div className="grid grid-cols-2 gap-2">
              {data.raidStats.map((m, i) => (
                <KCard key={i} label={m.label} value={m.value}
                  onChange={(v) => setArrayItem("raidStats", i, { value: v })}
                  readOnly={readOnly}/>
              ))}
            </div>
          </div>
        </div>
      </ActiveSub>

      {/* ─── CHANGE & ADOPTION ─── */}
      <ActiveSub id="ca">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
          {data.changeKpis.map((m, i) => (
            <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
              onChange={(v) => setArrayItem("changeKpis", i, { value: v })}
              onChangeSub={(v) => setArrayItem("changeKpis", i, { sub: v })}
              readOnly={readOnly}/>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-4">
            <SectionTitle>Business readiness by region</SectionTitle>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart layout="vertical" data={data.readiness} margin={{ top: 8, right: 10, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                  <XAxis type="number" domain={[0, 100]} stroke="var(--tx3)" fontSize={11} tickFormatter={v => `${v}%`}/>
                  <YAxis type="category" dataKey="region" stroke="var(--tx3)" fontSize={11} width={110}/>
                  <Tooltip contentStyle={{ background: "var(--paper)", border: "1px solid var(--border)", fontSize: 12 }} formatter={(v) => `${v}%`}/>
                  <Bar dataKey="pct" name="Readiness %" radius={[0,4,4,0]}>
                    {data.readiness.map((r, i) => <Cell key={i} fill={TONE_HEX[r.tone] || "#0369A1"}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="card p-4">
            <SectionTitle>OCM workstream — click status to cycle</SectionTitle>
            <table className="tbl">
              <thead><tr><th>Deliverable</th><th>Status</th></tr></thead>
              <tbody>
                {data.ocm.map((o, i) => (
                  <tr key={i}>
                    <td><Editable value={o.item} onChange={(v) => setArrayItem("ocm", i, { item: v })} disabled={readOnly}/></td>
                    <td>
                      <button type="button" disabled={readOnly} onClick={() => setArrayItem("ocm", i, { status: nextStatus(o.status, "ocm") })}>
                        <Pill tone={STATUS_TO_TONE[o.status] || "neutral"}>{o.status}</Pill>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <hr className="border-[var(--border)] my-4"/>
            <div className="grid grid-cols-2 gap-2">
              {data.changeFooter.map((m, i) => (
                <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
                  onChange={(v) => setArrayItem("changeFooter", i, { value: v })}
                  onChangeSub={(v) => setArrayItem("changeFooter", i, { sub: v })}
                  readOnly={readOnly}/>
              ))}
            </div>
          </div>
        </div>
      </ActiveSub>

      {/* ─── WORKSHOP LEADERSHIP ─── */}
      <ActiveSub id="ws">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
          {data.workshopKpis.map((m, i) => (
            <KCard key={i} label={m.label} value={m.value} sub={m.sub} tone={m.tone}
              onChange={(v) => setArrayItem("workshopKpis", i, { value: v })}
              onChangeSub={(v) => setArrayItem("workshopKpis", i, { sub: v })}
              readOnly={readOnly}/>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <SectionTitle>Leadership split — you vs delegated</SectionTitle>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="card p-4 text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-500">You lead</div>
                <div className="font-display text-3xl font-extrabold mt-1" style={{ color: "#0EA5E9" }}>
                  <Editable value={`${data.leadershipSplit.youPct}%`} onChange={(v) => {
                    const n = parseInt(v); if (!isNaN(n)) setField("leadershipSplit", { ...data.leadershipSplit, youPct: n });
                  }} disabled={readOnly}/>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">
                  <Editable value={data.leadershipSplit.youCount} onChange={(v) => setField("leadershipSplit", { ...data.leadershipSplit, youCount: v })} disabled={readOnly}/>
                </div>
              </div>
              <div className="card p-4 text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-zinc-500">Directors / leads</div>
                <div className="font-display text-3xl font-extrabold mt-1" style={{ color: "#14B8A6" }}>
                  <Editable value={`${data.leadershipSplit.delPct}%`} onChange={(v) => {
                    const n = parseInt(v); if (!isNaN(n)) setField("leadershipSplit", { ...data.leadershipSplit, delPct: n });
                  }} disabled={readOnly}/>
                </div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">
                  <Editable value={data.leadershipSplit.delCount} onChange={(v) => setField("leadershipSplit", { ...data.leadershipSplit, delCount: v })} disabled={readOnly}/>
                </div>
              </div>
            </div>
            <div className="card p-4">
              <SectionTitle>Your workshop outcomes QTD</SectionTitle>
              <table className="tbl">
                <thead><tr><th>Outcome</th><th>Count</th><th>Trend</th></tr></thead>
                <tbody>
                  {data.outcomes.map((o, i) => (
                    <tr key={i}>
                      <td><Editable value={o.o}     onChange={(v) => setArrayItem("outcomes", i, { o: v })}     disabled={readOnly}/></td>
                      <td className="font-mono font-semibold"><Editable value={o.count} onChange={(v) => setArrayItem("outcomes", i, { count: v })} disabled={readOnly}/></td>
                      <td className="text-[11px]" style={{ color: TONE_HEX[o.tone] || "#71717A" }}>
                        <Editable value={o.trend} onChange={(v) => setArrayItem("outcomes", i, { trend: v })} disabled={readOnly}/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card p-4">
            <SectionTitle>Executive workshop catalog</SectionTitle>
            <div className="space-y-2.5">
              {data.catalog.map((c, i) => (
                <div key={i} className="p-3 rounded-sm bg-[var(--surface)] border border-[var(--border)]">
                  <div className="font-display text-[13px] font-semibold text-zinc-900">
                    <Editable value={c.name} onChange={(v) => setArrayItem("catalog", i, { name: v })} disabled={readOnly}/>
                  </div>
                  <div className="text-[11.5px] text-zinc-600 mt-1 leading-relaxed">
                    <Editable value={c.desc} onChange={(v) => setArrayItem("catalog", i, { desc: v })} disabled={readOnly} multiline/>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {c.tags.map((t, j) => (
                      <span key={j} className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-mono bg-white border border-[var(--border)] text-[var(--navy)]">{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <hr className="border-[var(--border)] my-5"/>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card p-4">
            <SectionTitle>Signature questions you drive</SectionTitle>
            <div className="space-y-1.5">
              {data.questions.map((q, i) => (
                <div key={i} className="text-[12.5px] text-zinc-800 leading-relaxed pl-3 border-l-2 border-[var(--navy)] bg-[var(--surface)] py-1.5 pr-2 rounded-r-sm">
                  <Editable value={q} onChange={(v) => patch(prev => ({ questions: prev.questions.map((x, j) => j === i ? v : x) }))} disabled={readOnly}/>
                </div>
              ))}
            </div>
          </div>
          <div className="card p-5" style={{ background: "var(--navy-deep)" }}>
            <div className="font-mono text-[9.5px] uppercase tracking-[0.12em] mb-2" style={{ color: "rgba(255,255,255,0.45)" }}>
              Interview positioning — strong executive answer
            </div>
            <div className="italic text-[11.5px] mb-3" style={{ color: "rgba(255,255,255,0.55)" }}>
              "Do you conduct workshops at your level?"
            </div>
            <div className="text-[13px] leading-[1.7]" style={{ color: "rgba(255,255,255,0.92)" }}>
              <Editable value={data.interviewQuote} onChange={(v) => setField("interviewQuote", v)} disabled={readOnly} multiline className="block whitespace-pre-wrap"/>
            </div>
          </div>
        </div>
      </ActiveSub>
    </>
  );
}
